import { Api } from "@penn/web-common";
import { unwrap } from "@penn/web-common";
import type { RegionCode } from "./filter";
import type { MachineCostMode, ModelConstantOverrides } from "./calculate";

// =============================================================================
// Optimize Allocation API client (svc-10, /v1/optimize-allocation/*).
//
// Q-OPT-03 D — ALWAYS async: enqueue a job (`POST .../jobs`), then poll
// (`GET .../jobs/:id`) until terminal. Cancel via `DELETE .../jobs/:id`.
// =============================================================================

export type OptimizeJobStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

export type OptimizeAssignmentEntry = {
  applicationId: string;
  productId: string;
};

export type OptimizeRequest = {
  optimizeSessionId: string;
  region: RegionCode;
  machineCostMode: MachineCostMode;
  applicationIds: string[];
  machineIds: string[];
  selectedMachineByApp?: Record<string, string>;
  modelConstantOverridesByProductId?: Record<string, ModelConstantOverrides>;
  machinePriceOverrideByProductId?: Record<string, string>;
  /** Configure price keyed by `${productId}|${sortedAppIds}`. */
  machinePriceOverrideByGroupKey?: Record<string, string>;
  /** CR-006: full configured investment per (machine, app-subset), scored once. */
  configuredInvestmentByGroupKey?: Record<string, string>;
};

export type OptimizeResult = {
  assignment: OptimizeAssignmentEntry[];
  totalCost: string;
  alternatives?: Array<{
    totalCost: string;
    assignment: OptimizeAssignmentEntry[];
  }>;
  cache: { rowsRead: number; rowsMissing: number };
  breakdown?: unknown;
};

export type OptimizeEnqueueResponse = {
  ok: true;
  jobId: string;
  status: OptimizeJobStatus;
  totalCombinations: number;
  processedCombinations?: number;
};

export type OptimizeJobStatusResponse = {
  ok: true;
  jobId: string;
  status: OptimizeJobStatus;
  processedCombinations: number;
  totalCombinations: number;
  result?: OptimizeResult;
  error?: { code: string; message: string };
};

export async function postOptimizeJob(
  body: OptimizeRequest,
  signal?: AbortSignal,
): Promise<OptimizeEnqueueResponse> {
  const res = await Api.post("/v1/optimize-allocation/jobs", body, { signal });
  return unwrap<OptimizeEnqueueResponse>(res.data);
}

export async function getOptimizeJob(
  jobId: string,
  signal?: AbortSignal,
): Promise<OptimizeJobStatusResponse> {
  const res = await Api.get(`/v1/optimize-allocation/jobs/${jobId}`, {
    signal,
  });
  return unwrap<OptimizeJobStatusResponse>(res.data);
}

export async function cancelOptimizeJob(
  jobId: string,
): Promise<OptimizeJobStatusResponse> {
  const res = await Api.delete(`/v1/optimize-allocation/jobs/${jobId}`);
  return unwrap<OptimizeJobStatusResponse>(res.data);
}
